import React, { useEffect, useRef, useState } from 'react';
import type { Application } from '@splinetool/runtime';
import { screenTransform, SceneControls, SceneObject, Point } from '../lib/spline-screen';

const SCENE = 'https://prod.spline.design/0IscUcpQouT5N5nL/scene.splinecode';
export const KRYS_TALK_BUTTON = 'Power button';
const MYSTERY_BOX_LIFT = 22;
const MYSTERY_BOX_RISE = 40;
const AUTHORED_BOX_RISE = 62;
// IDs read from the original scene's Buttons below screen group.
const RETURN_BUTTON = 'b8364c15-2bfd-47b9-bfb1-63337a1a2bb7';
const PLAY_BUTTON = 'bcf93abb-16e6-4c09-add9-6025fb64eb51';

interface Props {
  onTalk: () => void;
  onSend: () => void;
  onBack: () => void;
  children: React.ReactNode;
}

export default function KrysScene({ onTalk, onSend, onBack, children }: Props) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const actions = useRef({ onTalk, onSend, onBack });
  actions.current = { onTalk, onSend, onBack };
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!canvas.current) return;
    let disposed = false;
    let app: Application | undefined;
    let frame = 0;
    let resize: ResizeObserver | undefined;
    let removePointerEvents = () => {};
    const controller = new AbortController();
    setState('loading');
    const timeout = window.setTimeout(() => {
      controller.abort();
      if (!disposed) setState('error');
    }, 45_000);

    const load = async () => {
      try {
        const { Application } = await import('@splinetool/runtime');
        if (disposed || controller.signal.aborted || !canvas.current) return;
        app = new Application(canvas.current, { renderer: 'webgl', renderMode: 'continuous' });
        await app.load(SCENE, undefined, { signal: controller.signal });
        if (disposed || controller.signal.aborted) { app.dispose(); return; }
        // Use the scene's authored front-facing pose. Its base pose is the closed back.
        const game = app.findObjectByName('Game');
        if (game) game.state = 'State';
        app.setGlobalEvents(false);
        const controls = app.controls as SceneControls;
        const orbit = controls.orbitControls;
        if (!orbit) throw new Error('Scene camera unavailable');
        // Page scrolling should never zoom or push the device out of the frame.
        orbit.enableZoom = false;
        orbit.enablePan = false;
        orbit.enabled = false;
        const fit = () => {
          const element = canvas.current;
          if (!element) return;
          // The template grows with wide viewports. Reserve vertical room for
          // the complete box + prize animation, including in landscape layouts.
          const landscapeScale = Math.min(1, element.clientHeight / element.clientWidth);
          app?.setZoom((element.clientWidth < 480 ? 1.4 : 0.94) * landscapeScale);
        };
        fit();
        resize = new ResizeObserver(fit);
        resize.observe(canvas.current);
        const camera = orbit.object;
        let root = camera;
        while (root.parent) root = root.parent as typeof root;
        const dockedBoxes: { box: SceneObject; group: SceneObject; restY: number }[] = [];
        // Keep the display offset outside the authored animation.
        // Spline can now return the box to its base state without hiding it.
        for (const name of ['mystery box 1', 'mystery box 2']) {
          const box = root.getObjectByName(name);
          if (!box?.parent) continue;
          const lift = await app.createObject('Group', {
            name: `${name} display offset`, parent: box.parent.uuid, position: [0, MYSTERY_BOX_LIFT, 0],
          });
          if (disposed || controller.signal.aborted) { app.dispose(); return; }
          const group = root.getObjectByName(lift.name);
          if (!group) throw new Error('Mystery box display offset unavailable');
          group.add(box);
          dockedBoxes.push({ box, group, restY: box.position.y });
        }
        const screen = root.getObjectByName('SCREEN inner');
        screen?.geometry?.computeBoundingBox();
        const bounds = screen?.geometry?.boundingBox;
        if (!screen || !bounds) throw new Error('Console screen unavailable');
        const center = orbit.target.clone();
        const distance = Math.hypot(camera.position.x - center.x, camera.position.y - center.y, camera.position.z - center.z);
        // Aim higher to place the whole console lower inside the actual canvas,
        // leaving headroom for the fully extended mushroom and coin.
        center.y += 65;
        // The authored open console faces +Z with +Y up. Do not sample the
        // opening animation: an intermediate pose bakes its tilt into the camera.
        const normal = camera.position.clone().set(0, 0, 1);
        const right = camera.position.clone().set(1, 0, 0);
        const up = camera.position.clone().set(0, 1, 0);
        const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const pointer = { x: 0, y: 0 };
        const tilt = { x: 0, y: 0 };
        const trackPointer = (event: PointerEvent) => {
          if (event.pointerType !== 'mouse' || motion.matches) return;
          const rect = canvas.current?.getBoundingClientRect();
          if (!rect) return;
          pointer.x = Math.max(-1, Math.min(1, (event.clientX - rect.left) / rect.width * 2 - 1));
          pointer.y = Math.max(-1, Math.min(1, (event.clientY - rect.top) / rect.height * 2 - 1));
        };
        const resetPointer = () => { pointer.x = 0; pointer.y = 0; };
        window.addEventListener('pointermove', trackPointer, true);
        window.addEventListener('blur', resetPointer);
        document.documentElement.addEventListener('pointerleave', resetPointer);
        const corners = [
          [bounds.min.x, bounds.max.y], [bounds.max.x, bounds.max.y],
          [bounds.max.x, bounds.min.y], [bounds.min.x, bounds.min.y],
        ];
        // Project against the actual 3D mesh every frame, so the UI stays in its screen
        // through camera motion, model animation and browser resizing.
        const animateCamera = () => {
          if (disposed || !canvas.current || !surface.current) return;
          // Preserve the authored box-first / prize-second sequence, scaling only
          // the box's 62-unit rise so its bottom remains seated in the console.
          for (const { box, group, restY } of dockedBoxes) {
            const travel = box.position.y - restY;
            const rise = Math.max(-4, Math.min(MYSTERY_BOX_RISE, travel * MYSTERY_BOX_RISE / AUTHORED_BOX_RISE));
            group.position.y = MYSTERY_BOX_LIFT - travel + rise;
            group.updateMatrix();
          }
          screen.updateWorldMatrix(true, false);
          tilt.x += ((motion.matches ? 0 : pointer.x * 0.065) - tilt.x) * 0.075;
          tilt.y += ((motion.matches ? 0 : pointer.y * 0.045) - tilt.y) * 0.075;
          camera.position.set(
            center.x + distance * (normal.x + right.x * tilt.x + up.x * tilt.y),
            center.y + distance * (normal.y + right.y * tilt.x + up.y * tilt.y),
            center.z + distance * (normal.z + right.z * tilt.x + up.z * tilt.y),
          );
          camera.up.set(up.x, up.y, up.z);
          camera.lookAt(center);
          camera.updateMatrixWorld();
          frame = requestAnimationFrame(animateCamera);
        };
        const projectScreen = () => {
          if (disposed || !canvas.current || !surface.current) return;
          const width = canvas.current.clientWidth, height = canvas.current.clientHeight;
          const projected = corners.map(([x, y]) => camera.position.clone()
            .set(x, y, bounds.max.z + 0.1).applyMatrix4(screen.matrixWorld).project(camera));
          const points = projected.map(p => ({ x: (p.x + 1) * width / 2, y: (1 - p.y) * height / 2 })) as [Point, Point, Point, Point];
          const [a, b, , d] = points;
          const front = (b.x - a.x) * (d.y - a.y) - (b.y - a.y) * (d.x - a.x) > 0;
          const logicalWidth = Math.max(260, Math.round(Math.hypot(b.x - a.x, b.y - a.y)));
          const logicalHeight = Math.max(160, Math.round(Math.hypot(d.x - a.x, d.y - a.y)));
          const transform = screenTransform(points, logicalWidth, logicalHeight);
          // Imperative geometry only; visual styling stays in Tailwind/CSS.
          surface.current.style.width = `${logicalWidth}px`;
          surface.current.style.height = `${logicalHeight}px`;
          surface.current.style.transform = transform || '';
          surface.current.style.visibility = front && transform && projected.every(p => p.z > -1 && p.z < 1) ? 'visible' : 'hidden';

        };
        // Some template buttons have no authored Spline event, so hit-test the
        // actual scene meshes. Native Spline animation/audio handlers still run.
        const element = canvas.current;
        let press: Point | undefined;
        const pointerDown = (event: PointerEvent) => { press = { x: event.clientX, y: event.clientY }; };
        const pointerUp = (event: PointerEvent) => {
          const start = press;
          press = undefined;
          if (!start || Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) return;
          const rect = element.getBoundingClientRect();
          const raycaster = controls.eventContext.raycaster;
          raycaster.setFromCamera({ x: (event.clientX - rect.left) / rect.width * 2 - 1, y: 1 - (event.clientY - rect.top) / rect.height * 2 }, camera);
          if (surface.current?.style.visibility !== 'visible') return;
          // The template's face shell overlaps its inset keys in depth; inspect
          // each ray hit for a key instead of stopping at that decorative shell.
          for (const hit of raycaster.intersectObjects(root.children, true)) {
            let object: SceneObject | undefined = hit.object;
            while (object?.visible) {
              if (object.uuid === RETURN_BUTTON) { actions.current.onBack(); return; }
              if (object.uuid === PLAY_BUTTON || object.name === 'Button edge') { actions.current.onSend(); return; }
              if (object.name === KRYS_TALK_BUTTON) { actions.current.onTalk(); return; }
              object = object.parent;
            }
          }
        };
        element.addEventListener('pointerdown', pointerDown);
        element.addEventListener('pointerup', pointerUp);
        removePointerEvents = () => {
          app?.removeEventListener('rendered', projectScreen);
          window.removeEventListener('pointermove', trackPointer, true);
          window.removeEventListener('blur', resetPointer);
          document.documentElement.removeEventListener('pointerleave', resetPointer);
          element.removeEventListener('pointerdown', pointerDown);
          element.removeEventListener('pointerup', pointerUp);
        };
        app.addEventListener('rendered', projectScreen);
        setState('ready');
        frame = requestAnimationFrame(animateCamera);
      } catch {
        if (!disposed) setState('error');
      } finally { window.clearTimeout(timeout); }
    };
    void load();
    return () => {
      disposed = true;
      controller.abort();
      cancelAnimationFrame(frame);
      resize?.disconnect();
      window.clearTimeout(timeout);
      removePointerEvents();
      app?.dispose();
    };
  }, [attempt]);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <canvas ref={canvas} aria-label="Krys's interactive 3D game console" className={`h-full w-full ${state === 'ready' ? '' : 'invisible'}`} />
      <div ref={surface} hidden={state !== 'ready'} aria-label="Game console screen" className={`absolute left-0 top-0 origin-top-left overflow-hidden rounded-[14px] ${state === 'ready' ? '' : 'invisible'}`}
        onPointerDown={event => event.stopPropagation()} onPointerMove={event => event.stopPropagation()} onWheel={event => event.stopPropagation()}>
        {children}
      </div>
      {state !== 'ready' && <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-8 text-center text-[#F1EFE0]/75" role="status">
        <p>{state === 'loading' ? 'Loading the console…' : 'The 3D console could not load.'}</p>
        {state === 'error' && <button className="rounded-full border border-[#F1EFE0]/30 px-5 py-2 hover:bg-white/10" onClick={() => setAttempt(n => n + 1)}>Reload console</button>}
      </div>}
    </div>
  );
}
